import { useParams, Link } from "react-router-dom";
import { useResearchItem, useResearchItemTags, useTransitionStatus, type ResearchStatus } from "@/hooks/useResearch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { format } from "date-fns";
import { toast } from "sonner";
import ResearchComments from "@/components/research/ResearchComments";
import ResearchReviewers from "@/components/research/ResearchReviewers";
import ResearchAIAssist from "@/components/research/ResearchAIAssist";
import ResearchExportButtons from "@/components/research/ResearchExportButtons";
import ICPackPanel from "@/components/research/ICPackPanel";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

const NEXT: Record<ResearchStatus, ResearchStatus[]> = {
  draft: ["in_review", "archived"],
  in_review: ["approved", "rejected", "draft"],
  approved: ["published", "archived"],
  published: ["archived"],
  archived: ["draft"],
  rejected: ["draft", "archived"],
};

export default function ResearchDetail() {
  const { id } = useParams();
  const { data: item, isLoading } = useResearchItem(id);
  const { data: tags } = useResearchItemTags(id);
  const transition = useTransitionStatus();
  const { enabled: icPackEnabled } = useFeatureFlag("phase_8_ic_pack");



  if (isLoading) return <p className="text-sm text-muted-foreground">Loading...</p>;
  if (!item) return <p className="text-sm text-muted-foreground">Not found</p>;

  const next = NEXT[item.status] ?? [];

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-primary">{item.title}</h2>
          {item.summary && <p className="text-muted-foreground mt-1">{item.summary}</p>}
          <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
            <Badge>{item.status}</Badge>
            <span>Updated {format(new Date(item.updated_at), "dd/MM/yyyy HH:mm")}</span>
            {item.primary_axis && <span>· {item.primary_axis}</span>}
            {item.conviction && <span>· Conviction {item.conviction}/5</span>}
            {item.time_horizon && <span>· {item.time_horizon}</span>}
            {item.portfolio_level && <span>· P{item.portfolio_level}</span>}
          </div>
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {tags.map((t: any) => (
                <Badge key={t.taxonomy_value_id} variant="outline">
                  {t.research_taxonomy_values?.axis}: {t.research_taxonomy_values?.value}
                </Badge>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <Button asChild variant="outline"><Link to={`/partner/research/${id}/edit`}>Edit</Link></Button>
          <ResearchExportButtons item={item} />
          {next.map((s) => (
            <Button
              key={s}
              size="sm"
              variant="secondary"
              onClick={async () => {
                try {
                  await transition.mutateAsync({ id: item.id, status: s });
                  toast.success(`Moved to ${s}`);
                } catch (e: any) {
                  toast.error(e.message);
                }
              }}
            >
              → {s}
            </Button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          {item.body_html ? (
            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.body_html) }} />
          ) : (
            <p className="text-muted-foreground text-sm">No content yet.</p>
          )}
        </CardContent>
      </Card>

      {icPackEnabled && <ICPackPanel item={item} />}



      <ResearchAIAssist
        itemId={item.id}
        aiSummary={(item as any).ai_summary}
        aiRisks={(item as any).ai_risks}
        aiHouseView={(item as any).ai_house_view_draft}
        aiGeneratedAt={(item as any).ai_generated_at}
      />

      <ResearchReviewers itemId={item.id} itemTitle={item.title} authorId={item.author_id} />
      <ResearchComments itemId={item.id} itemTitle={item.title} authorId={item.author_id} />
    </div>
  );
}
